import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { brand } from "@/config/brand";
import { useClientAuth } from "@/auth/ClientAuthProvider";
import { useMagicLink } from "@/auth/useMagicLink";
import { safeNextPath } from "@/auth/nextPath";
import { useDataSource } from "@/data/DataProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Splash } from "@/components/chrome/Splash";
import { NoAccount } from "@/screens/NoAccount";

export default function Login() {
  const { access, refresh } = useClientAuth();
  const { mode } = useDataSource();
  const { sendMagicLink, verifyCode, codeSignIn, loading, error } = useMagicLink();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get("next"));
  // 6c.5: the one-tap link from the welcome email could not sign her in (src/screens/SignIn.tsx)
  const linkNotice =
    params.get("link") === "gone"
      ? "That link has already been used or has expired. We'll email you a code instead."
      : params.get("link") === "failed"
        ? "We couldn't sign you in with that link. Enter your email and we'll send you a code."
        : null;
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  if (access.state === "loading") return <Splash label="Checking your session" />;
  if (access.state === "ready") return <Navigate to={next ?? "/"} replace />;
  if (access.state === "no_client") return <NoAccount message={access.message} email={access.user.email ?? null} />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    if (await sendMagicLink(trimmed, next)) setSent(true);
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    // Signed in: the check below turns "ready" and sends them on to where they were going.
    if (await verifyCode(email.trim(), code)) await refresh();
    else setCode("");
  };

  const resendCode = async () => {
    setCode("");
    if (await sendMagicLink(email.trim(), next)) toast.success("We sent a new code.");
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-lg flex-col px-5 pb-safe pt-safe">
      <div className="flex flex-1 flex-col justify-center py-10">
        <img src={brand.logoPath} alt={brand.name} className="mb-10 h-9 w-auto self-start" />
        {linkNotice && !sent ? (
          <p role="status" className="mb-4 rounded-lg bg-tint-soft px-4 py-3 text-subheadline text-text-primary">
            {linkNotice}
          </p>
        ) : null}

        {sent && codeSignIn ? (
          <section className="rounded-xl border border-line bg-card p-6 shadow-card">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-primary">
              <Mail className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-ink">Check your email</h1>
            <p className="mt-2 text-muted-foreground">
              We sent a six-digit code to <span className="font-semibold text-ink">{email.trim()}</span>. Enter it here to sign in.
            </p>
            <form onSubmit={submitCode} className="mt-5 space-y-3">
              <label htmlFor="code" className="sr-only">
                Sign-in code
              </label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center text-2xl font-semibold tracking-[0.4em]"
                required
                autoFocus
              />
              <Button type="submit" size="lg" className="w-full" disabled={loading || code.length !== 6}>
                {loading ? <Loader2 className="animate-spin" /> : <ShieldCheck />}
                Sign in
              </Button>
            </form>
            <p className="mt-4 text-sm text-muted-foreground">The code works for 15 minutes. Check spam if you do not see it.</p>
            <Button variant="ghost" className="mt-2 w-full" onClick={() => void resendCode()} disabled={loading}>
              Send a new code
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                setSent(false);
                setCode("");
              }}
            >
              Use a different email
            </Button>
          </section>
        ) : sent ? (
          <section className="rounded-xl border border-line bg-card p-6 shadow-card">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-primary">
              <Mail className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-ink">Check your email</h1>
            <p className="mt-2 text-muted-foreground">
              We sent a secure sign-in link to <span className="font-semibold text-ink">{email.trim()}</span>. Open it on this
              phone and you are in.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">The link expires in 1 hour. Check spam if you do not see it.</p>
            {mode === "demo" ? (
              <Button asChild className="mt-6 w-full">
                <Link to="/auth/callback">Open the demo sign-in link</Link>
              </Button>
            ) : null}
            <Button variant="ghost" className="mt-2 w-full" onClick={() => setSent(false)}>
              Use a different email
            </Button>
          </section>
        ) : (
          <section>
            <h1 className="text-[32px] font-extrabold leading-tight tracking-tight text-ink">Your plan, in your pocket.</h1>
            <p className="mt-3 text-muted-foreground">
              Sign in with the email your strategist has on file. No password, just {codeSignIn ? "a code we email you" : "a secure link"}.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-3">
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              <Button type="submit" size="lg" className="w-full" disabled={loading || !email.trim()}>
                {loading ? <Loader2 className="animate-spin" /> : <ShieldCheck />}
                {codeSignIn ? "Email me a sign-in code" : "Send my sign-in link"}
              </Button>
            </form>

            <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
              Do not have access yet? Your strategist sends invitations from your file. Call{" "}
              <a className="font-semibold text-primary" href={brand.supportPhoneHref}>
                {brand.supportPhone}
              </a>
              .
            </p>
          </section>
        )}
      </div>

      <footer className="pb-4 text-center text-[11px] leading-relaxed text-muted-foreground">
        {brand.disclosure}{" "}
        <a className="underline" href={brand.privacyUrl} target="_blank" rel="noreferrer">
          Privacy
        </a>{" "}
        ·{" "}
        <a className="underline" href={brand.termsUrl} target="_blank" rel="noreferrer">
          Terms
        </a>
      </footer>
    </main>
  );
}
